/**
 * Manager Login Page
 * Handles passkey authentication for existing managers
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import {
  managerLoginOptions,
  managerLoginVerify,
  managerSession,
  managerRequestMagicLink,
  managerRequestEmailCode,
  ApiClientError,
} from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { createLogger } from '@resida/shared';
import { Button } from '../../components/ui/button';
import { validateReturnTo } from '../../lib/return-to';

const logger = createLogger('landlord:pages:auth:LoginPage');

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();

  // Read returnTo from URL query params (set by apiFetch REAUTHENTICATION_REQUIRED interceptor)
  const returnTo = new URLSearchParams(location.search).get('returnTo');

  const [email, setEmail] = useState('');
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  /**
   * Handle passkey login (discoverable credentials)
   */
  async function handlePasskeyLogin() {
    setError(null);
    setIsLoading(true);

    try {
      // Step 1: Get login options (no email = discoverable flow)
      const { options, sessionId } = await managerLoginOptions();

      // Step 2: Start WebAuthn authentication
      const credential = await startAuthentication({ optionsJSON: options });

      // Step 3: Verify with server
      await managerLoginVerify(credential, sessionId);

      // Step 4: Fetch session and update auth context
      const session = await managerSession();
      if (session) {
        setSession(session);
      }

      // Step 5: Redirect to returnTo or dashboard
      navigate(validateReturnTo(returnTo));
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle email code request
   */
  async function handleEmailCodeRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await managerRequestEmailCode(email);
      navigate('/auth/email-code/verify', { state: { email, purpose: 'login', returnTo } });
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle magic link request
   */
  async function handleMagicLinkRequest(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await managerRequestMagicLink(email, returnTo ?? undefined);
      setMagicLinkSent(true);
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  }

  /**
   * Handle and display errors
   */
  function handleError(err: unknown) {
    logger.error('Login error:', err);

    if (err instanceof ApiClientError) {
      if (err.code === 'USER_NOT_FOUND') {
        setError('No account found. Please register first.');
      } else if (err.code === 'INVALID_CREDENTIAL') {
        setError('Authentication failed. Please try again.');
      } else {
        setError(err.message);
      }
    } else if (err instanceof Error) {
      // WebAuthn errors
      if (err.name === 'NotAllowedError') {
        setError('Authentication was cancelled. Please try again.');
      } else if (err.name === 'InvalidStateError') {
        setError('No passkey found for this device.');
      } else if (err.name === 'NotSupportedError') {
        setError('Your browser or device does not support passkeys.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
      }
    } else {
      setError('An unexpected error occurred. Please try again.');
    }
  }

  /**
   * Validate email format
   */
  function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center px-4 py-8" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-sm border border-border p-6 md:p-8">
          <h1 className="text-2xl font-semibold text-foreground mb-2 text-center">
            Welcome Back
          </h1>
          <p className="text-sm text-muted-foreground mb-6 text-center">
            Sign in to your landlord portal
          </p>

          {error && (
            <div
              className="text-sm text-destructive p-3 bg-destructive/5 border border-destructive/20 rounded-md mb-4"
              role="alert"
              id="login-error"
              data-testid="error-message"
            >
              {error}
            </div>
          )}

          {magicLinkSent ? (
            <div className="text-center" data-testid="magic-link-sent">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-success text-success-foreground text-4xl flex items-center justify-center font-bold" aria-hidden="true">
                &#x2713;
              </div>
              <h2 className="text-xl font-semibold text-foreground mb-4">
                Check Your Email
              </h2>
              <p className="text-sm text-foreground mb-3">
                We've sent a magic link to <strong>{email}</strong>. Click the link in the email to
                sign in.
              </p>
              <p className="text-xs text-muted-foreground mb-6">
                The link will expire in 15 minutes.
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setMagicLinkSent(false);
                  setEmail('');
                  setShowEmailInput(false);
                }}
                className="w-full"
                data-testid="back-to-login-button"
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-3">
                <Button
                  onClick={handlePasskeyLogin}
                  disabled={isLoading}
                  className="w-full"
                  data-testid="passkey-login-button"
                  aria-busy={isLoading}
                >
                  {isLoading ? 'Authenticating...' : 'Sign in with Passkey'}
                </Button>

                {!showEmailInput && (
                  <Button
                    variant="outline"
                    onClick={() => setShowEmailInput(true)}
                    disabled={isLoading}
                    className="w-full"
                    data-testid="use-email-button"
                  >
                    Use email instead
                  </Button>
                )}
              </div>

              {showEmailInput && (
                <div className="flex flex-col gap-4 mt-4">
                  <div className="flex items-center my-2" role="separator" aria-hidden="true">
                    <div className="flex-1 border-t border-border" />
                    <span className="px-4 text-xs text-muted-foreground">
                      or sign in with email
                    </span>
                    <div className="flex-1 border-t border-border" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="email" className="text-sm font-medium text-muted-foreground">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      disabled={isLoading}
                      required
                      aria-required="true"
                      aria-invalid={error !== null}
                      aria-describedby={error ? 'login-error' : undefined}
                      className="w-full min-h-[44px] px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring disabled:opacity-60"
                      data-testid="email-input"
                    />
                  </div>

                  <form onSubmit={handleMagicLinkRequest} data-testid="magic-link-form">
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={isLoading || !isValidEmail(email)}
                      className="w-full"
                      data-testid="magic-link-submit"
                    >
                      {isLoading ? 'Sending Link...' : 'Send Magic Link'}
                    </Button>
                  </form>

                  <form onSubmit={handleEmailCodeRequest}>
                    <Button
                      type="submit"
                      variant="outline"
                      disabled={isLoading || !isValidEmail(email)}
                      className="w-full"
                      data-testid="request-email-code-btn"
                    >
                      {isLoading ? 'Sending Code...' : 'Email me a code'}
                    </Button>
                  </form>
                </div>
              )}

              <p className="mt-6 text-sm text-muted-foreground text-center">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-primary hover:text-primary/80 underline"
                >
                  Create one
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
